import { React, useEffect, useState, useRef } from 'react'
import { CButton, CSpinner, CBadge, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CNav, CNavItem, CNavLink, CTabContent, CTabPane, CCardBody } from '@coreui/react'
import GetDiagnosisData from '../../services/general/getDiagnosisData.api'
import ModifyPatientHistory from '../../services/general/modifyPatientHistory.api'
import './PatientHistoryForm.css' // Make sure to create and use this CSS file
import HighlightedText from './../../components/shared/HighlightedText'
import Carder from '../../components/shared/Carder'
import toast from 'react-hot-toast'
import { toastConfig } from '../../constants/other.config'
import Graytextarea from './../..//components/Graytextarea'
import { X } from 'lucide-react'

import Editor from 'react-simple-wysiwyg'


const PatientHistoryForm = ({ patientID, loading, setLoading, saveTreatsShell, saveTreatsShellForHospitalRecord, removeDiagnosisIdFromNote, setDiagnosisAddedPinger, updatePatientDiagnosis, diagnosis, allHistory, setAllHistory, getHistory, noteStructureRef, addDiagnosisIdsToNote, appointments, UpdateCheckForStatusData }) => {
  const [diagnosisData, setDiagnosisData] = useState([])
  const [textInput, setTextInput] = useState('')
  const [modalVisible, setModalVisible] = useState(false)
  const [possibleDiagnosis, setPossibleDiagnosis] = useState([])
  const [activeTab, setActiveTab] = useState('new')
  const [currentDate, setCurrentDate] = useState('')
  const [editRecordId, setEditRecordId] = useState(null)
  const detectedKeywordsRef = useRef(new Set())
  const [loadings, setLoadings] = useState(false)

  const [canBeEditableExistInNoteMetadata, setCanBeEditableExistInNoteMetadata] = useState('')
  const [deleteVisibleId, setDeleteVisibleId] = useState(null)
  const [showModalHighlight, setShowModalHighlight] = useState(false)
  const [editableHighlightData, setEditableHighlightData] = useState({})
  const [highlightedText, setHighlightedText] = useState([])
  const [originalHistoryText, setOriginalHistoryText] = useState('')
  const [activeHistorySelection, setActiveHistorySelection] = useState(null)
  const [showEditHtml, setShowEditHtml] = useState(false)
  const [tempHtmlContent, setTempHtmlContent] = useState('')
  const historyHighlightRef = useRef(null)

  const currentHistoryId = noteStructureRef.current?.metadata?.historyId

  const persistNoteStructure = async (showAlert = false) => {
    if (typeof saveTreatsShellForHospitalRecord === 'function') {
      await saveTreatsShellForHospitalRecord(showAlert, noteStructureRef.current)
      return
    }

    if (typeof saveTreatsShell === 'function') {
      await saveTreatsShell(showAlert)
    }
  }

  const getHighlightText = (highlight, sourceText = '') => {
    if (typeof highlight?.text === 'string' && highlight.text.length > 0) {
      return highlight.text
    }

    return sourceText.slice(highlight.start, highlight.end)
  }

  const normalizeHighlights = (highlights = [], currentText = '', sourceText = currentText) => {
    const shouldValidateText = typeof currentText === 'string'
    const textToValidate = shouldValidateText ? currentText : ''
    const textSource = typeof sourceText === 'string' ? sourceText : textToValidate

    const validHighlights = (highlights || [])
      .filter((h) => h && typeof h.start === 'number' && typeof h.end === 'number' && h.start < h.end)
      .map((h) => ({
        ...h,
        start: Math.max(0, h.start),
        end: Math.min(h.end, shouldValidateText ? textToValidate.length : Number.MAX_SAFE_INTEGER),
      }))
      .map((h) => ({
        ...h,
        text: getHighlightText(h, textSource),
      }))
      .filter((h) => !shouldValidateText || textToValidate.slice(h.start, h.end) === h.text)

    if (!validHighlights.length) return []

    const mergedHighlights = []
    validHighlights
      .sort((a, b) => a.start - b.start)
      .forEach((highlight) => {
        const last = mergedHighlights[mergedHighlights.length - 1]
        if (!last || highlight.start > last.end) {
          mergedHighlights.push({ ...highlight })
        } else {
          last.end = Math.max(last.end, highlight.end)
          last.text = textToValidate.slice(last.start, last.end)
        }
      })

    return mergedHighlights
  }

  const getDiagnosisMatches = (inputValue = textInput, diagnosisList = diagnosisData) => {
    const normalize = (str = '') => str.toLowerCase().replace(/\s+/g, ' ').trim()
    const sanitized = (inputValue || '').replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase()
    const inputKeywords = sanitized.split(/\s+/).filter(Boolean)
    const detectedKeywords = new Set()

    if (!inputKeywords.length) {
      return { result: [], detectedKeywords: [] }
    }

    const matchesDiagnosis = (diagnosisKeywords) => {
      return diagnosisKeywords.some((keyword) => {
        const parts = keyword.split(' ').filter(Boolean)
        if (!parts.length) return false

        if (parts.length === 1) {
          return inputKeywords.includes(parts[0])
        }

        for (let i = 0; i <= inputKeywords.length - parts.length; i++) {
          let match = true
          for (let j = 0; j < parts.length; j++) {
            if (inputKeywords[i + j] !== parts[j]) {
              match = false
              break
            }
          }
          if (match) return true
        }

        return false
      })
    }

    const filtered = (diagnosisList || []).filter((diag) => {
      const keywords = (diag?.Keywords || '')
        .split(',')
        .map((k) => k.trim().toLowerCase())
        .filter(Boolean)

      const isMatch = matchesDiagnosis(keywords)

      if (isMatch) {
        keywords.forEach((k) => detectedKeywords.add(k))
      }
      return isMatch
    })

    const normalizedDiagnosis = (diagnosis || []).map(normalize)
    const result = filtered.filter((item) => !normalizedDiagnosis.includes(normalize(item.Name)))

    return { result, detectedKeywords: Array.from(detectedKeywords) }
  }

  useEffect(() => {
    populatedHistoryIdRef.current = null // reset on patient change
    getDiagnosisData()
    getHistory()
    setCurrentDate(getFormattedDate())
  }, [patientID])


  useEffect(() => {
    if (!allHistory?.length) return
    const historyId = noteStructureRef.current?.metadata?.historyId
    if (!historyId) return
    const selectedHistory = allHistory.find(h => h.Id === historyId)
    if (selectedHistory) {
      setTextInput(selectedHistory.History || '')
      setOriginalHistoryText(selectedHistory.History || '')
      setEditRecordId(selectedHistory.Id)
    }
  }, [allHistory])


  useEffect(() => {
    handlePopulatingSameMetadataHisotry()
  }, [allHistory, currentHistoryId])

  const handleInputChange = (event) => {
    const nextText = event.target.value
    setTextInput(nextText)
    setHighlightedText((prev) => normalizeHighlights(prev, nextText, originalHistoryText))
  }

  const handleGetDiagnosis = async () => {
    const fetchedDiagnosis = await getDiagnosisData()
    filterDiagnosis(false, fetchedDiagnosis)
    setModalVisible((prev) => !prev)
  }

  const getDiagnosisData = async () => {
    let a = await GetDiagnosisData()
    let data = JSON.parse(a.d)
    const diagnosisList = data.Data || []
    setDiagnosisData(diagnosisList)
    return diagnosisList
  }

  const addDiagnosis = (toggleModal = true) => {
    let newDiagnosis = possibleDiagnosis.map((obj) => {
      return obj.Name.trim()
    })
    let historyDiagnosis = newDiagnosis.reduce((acc, item) => {
      let existingData = noteStructureRef.current?.information?.[item] || []
      if (!existingData.includes('Hospital Records')) {
        acc[item] = [...existingData, 'Hospital Records']
      } else {
        acc[item] = existingData
      }

      return acc
    }, {})

    noteStructureRef.current = {
      ...noteStructureRef.current,
      information: {
        ...noteStructureRef.current.information,
        ...historyDiagnosis,
      },
    }

    newDiagnosis = newDiagnosis.join(',')
    const diagnosisIds = possibleDiagnosis.map((item) => item.id)
    addDiagnosisIdsToNote(diagnosisIds)
    updatePatientDiagnosis(patientID, newDiagnosis)
    if (toggleModal) {
      setModalVisible(false)
    }
    setDiagnosisAddedPinger((p) => !p)
  }

  const handleRemoveDiagnosis = (indexToRemove) => {
    setPossibleDiagnosis((prevDiagnosis) => prevDiagnosis.filter((_, index) => index !== indexToRemove))
  }

  const filterDiagnosis = (defaultOpen = false, diagnosisList = diagnosisData) => {
    const { result, detectedKeywords } = getDiagnosisMatches(textInput, diagnosisList)
    detectedKeywordsRef.current = new Set(detectedKeywords)
    setPossibleDiagnosis(result)
    return result.length > 0
  }

  const getFormattedDate = () => {
    const today = new Date()
    const dd = String(today.getDate()).padStart(2, '0')
    const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
    const yyyy = today.getFullYear()
    return `${mm}/${dd}/${yyyy}`
  }

  const handleEditHospitalRecords = (item, tooChangeTab = true) => {
    if (tooChangeTab) {
      setActiveTab('new')
    }
    setTextInput(item?.History)
    setOriginalHistoryText(item?.History || '')
    setEditRecordId(item?.Id)
    setHighlightedText(normalizeHighlights(noteStructureRef?.current?.highlightDetails?.[item?.Id] || [], item?.History || '', item?.History || ''))
  }

  const populatedHistoryIdRef = useRef(null)

  const handlePopulatingSameMetadataHisotry = () => {
    const historyId = noteStructureRef.current?.metadata?.historyId
    if (!historyId) return
    if (populatedHistoryIdRef.current === historyId) return
    populatedHistoryIdRef.current = historyId

    setCanBeEditableExistInNoteMetadata(historyId)
    const findThisNoteHistory = allHistory.find((item) => item.Id === historyId)
    setHighlightedText(normalizeHighlights(noteStructureRef?.current?.highlightDetails?.[historyId] || [], findThisNoteHistory?.History || '', findThisNoteHistory?.History || ''))
    console.log('findThisNoteHistory', findThisNoteHistory)
    if (findThisNoteHistory) {
      setOriginalHistoryText(findThisNoteHistory.History || '')
      handleEditHospitalRecords(findThisNoteHistory, false)
    }
  }

  const handleDeleteHospitalHistory = async () => {
    await ModifyPatientHistory('DELETE', '', '', '', deleteVisibleId?.toString())
    setDeleteVisibleId(null)
    await getHistory()
    setTextInput('')
    setHighlightedText([])
    noteStructureRef.current = {
      ...noteStructureRef.current,
      highlightDetails: {
        ...(noteStructureRef.current.highlightDetails || {}),
        [deleteVisibleId]: [],
      },
    }
    setEditRecordId(null)
  }

  const waitForInsertedHistory = async ({ retries = 5, delay = 800 }) => {
    for (let i = 0; i < retries; i++) {
      const histories = await getHistory()
      if (Array.isArray(histories) && histories.length > 0) {
        return histories
      }
      await new Promise((res) => setTimeout(res, delay))
    }
    return null
  }

  const handleAddHistory = async () => {
    
    try {
      setLoadings(true)
      if (editRecordId !== null) {
        // edit logic wreiting here
        const newListWithUpdatedHistory = allHistory.map((item, i) => {
          if (item.Id === editRecordId) {
            return {
              ...item,
              History: textInput,
            }
          }
          return item
        })
        setAllHistory(newListWithUpdatedHistory)
        // let allData = await ModifyPatientHistory("UPDATE", editRecordId, textInput, currentDate);
        let allData = await ModifyPatientHistory('UPDATE', patientID, textInput, currentDate, editRecordId)
        const currentHighlights = normalizeHighlights(highlightedText, textInput, originalHistoryText)
        setHighlightedText(currentHighlights)
        noteStructureRef.current = {
          ...noteStructureRef.current,
          highlightDetails: {
            ...(noteStructureRef.current.highlightDetails || {}),
            [editRecordId]: currentHighlights,
          },
        }
        await persistNoteStructure(false)
        setOriginalHistoryText(textInput)
        toast.success('Saved successfully', toastConfig)
      } else {
        await ModifyPatientHistory('INSERT', patientID, textInput, currentDate)
        const allHistories = await waitForInsertedHistory({ retries: 6, delay: 700 })
        if (!allHistories) return

        const latestHistoryId = allHistories[allHistories.length - 1]?.Id

        if (!latestHistoryId) return

        const currentHighlights = normalizeHighlights(highlightedText, textInput, originalHistoryText)
        setHighlightedText(currentHighlights)
        noteStructureRef.current = {
          ...noteStructureRef.current,
          metadata: {
            ...noteStructureRef.current.metadata,
            historyId: latestHistoryId,
          },
          highlightDetails: {
            ...(noteStructureRef.current.highlightDetails || {}),
            [latestHistoryId]: currentHighlights,
          },
        }
        await persistNoteStructure(false)

        setAllHistory(allHistories)
        setCanBeEditableExistInNoteMetadata(latestHistoryId)
        setEditRecordId(latestHistoryId)
        setOriginalHistoryText(textInput)
      }

      UpdateCheckForStatusData({ HR: true }) 
    } catch (error) {
      console.log(error, 'something went wrong in adding history')
    } finally {
      setLoadings(false)
    }
  }
  // const addHistory = async () => {
  //   if (!textInput.trim()) {
  //     toast.error('Please fill the history', toastConfig)
  //     return
  //   }
  //   await getDiagnosisData()
  //   filterDiagnosis(true)

  //   UpdateCheckForStatusData({ HR: true })
  // }

const debounceRef = useRef(false)

const addHistory = async () => {
  if (debounceRef.current) return
  debounceRef.current = true

  setTimeout(() => {
    debounceRef.current = false
  }, 1000) // 1 sec lock

  if (!textInput.trim()) {
    toast.error('Please fill the history', toastConfig)
    return
  }

  const fetchedDiagnosis = await getDiagnosisData()
  filterDiagnosis(true, fetchedDiagnosis)
  UpdateCheckForStatusData({ HR: true })
}

  const formatFreeTypeHtml = (html = '') => {
    if (!html) return ''


    return (
      html
        .replace(/style="([^"]*)font-weight:\s*[\w]+;?([^"]*)"/gi, (_, before, after) => `style="${before}color: var(--ui-color-highlight, #ff9f1a); font-weight: 600;${after}"`)
        // Also handle plain <b> tags
        .replace(/<b>/gi, '<span style="color: var(--ui-color-highlight, #ff9f1a); font-weight: 600;">')
        .replace(/<\/b>/gi, '</span>')
        // Also handle <strong> tags
        .replace(/<strong(\s[^>]*)?>/gi, (_, attrs) => `<strong${attrs || ''} style="color: var(--ui-color-highlight, #ff9f1a); font-weight: 600;">`)
    )
  }


  const getTextOffset = (container, targetNode, targetOffset) => {
    let charCount = 0
    let foundTarget = false

    const walk = (node) => {
      if (foundTarget) return

      if (node === targetNode) {
        if (node.nodeType === Node.TEXT_NODE) {
          charCount += targetOffset
        } else {
          Array.from(node.childNodes)
            .slice(0, targetOffset)
            .forEach((child) => {
              charCount += child.textContent.length
            })
        }
        foundTarget = true
        return
      }

      if (node.nodeType === Node.TEXT_NODE) {
        charCount += node.textContent.length
        return
      }

      node.childNodes.forEach(walk)
    }

    walk(container)
    return charCount
  }

  const handleSelection = () => {
    const selection = window.getSelection()
    const selectedText = selection?.toString() || ''
    const text = selectedText.trim()

    if (!text || !selection.rangeCount) {
      setActiveHistorySelection(null)
      return
    }

    const range = selection.getRangeAt(0)
    const container = historyHighlightRef.current
    if (!container || !container.contains(range.startContainer) || !container.contains(range.endContainer)) {
      setActiveHistorySelection(null)
      return
    }

    const start = getTextOffset(container, range.startContainer, range.startOffset)
    const end = getTextOffset(container, range.endContainer, range.endOffset)
    if (end <= start) {
      setActiveHistorySelection(null)
      return
    }

    setActiveHistorySelection({ start, end, text: textInput.slice(start, end) })
  }

  const selectionFullyHighlighted = (selection, highlights = []) => {
    if (!selection || !highlights?.length) return false
    const normalizedHighlights = normalizeHighlights(highlights, textInput, textInput)
    let coveredUntil = selection.start

    for (const highlight of normalizedHighlights) {
      if (highlight.end <= coveredUntil) continue
      if (highlight.start > coveredUntil) return false
      coveredUntil = Math.max(coveredUntil, highlight.end)
      if (coveredUntil >= selection.end) return true
    }

    return false
  }

  const handleAddSelections = () => {
    if (!activeHistorySelection) return
    const alreadyExists = highlightedText.some((h) => h.start === activeHistorySelection.start && h.end === activeHistorySelection.end)
    if (!alreadyExists) {
      setHighlightedText((prev) => normalizeHighlights([...prev, { ...activeHistorySelection }], textInput, textInput))
    }
    window.getSelection().removeAllRanges()
    setActiveHistorySelection(null)
  }

  const handleRemoveSelections = () => {
    if (!activeHistorySelection) return
    setHighlightedText((prev) => {
      const nextHighlights = []
      normalizeHighlights(prev, textInput, textInput).forEach((highlight) => {
        if (activeHistorySelection.end <= highlight.start || activeHistorySelection.start >= highlight.end) {
          nextHighlights.push(highlight)
          return
        }

        if (activeHistorySelection.start > highlight.start) {
          nextHighlights.push({
            start: highlight.start,
            end: activeHistorySelection.start,
            text: textInput.slice(highlight.start, activeHistorySelection.start),
          })
        }

        if (activeHistorySelection.end < highlight.end) {
          nextHighlights.push({
            start: activeHistorySelection.end,
            end: highlight.end,
            text: textInput.slice(activeHistorySelection.end, highlight.end),
          })
        }
      })

      return normalizeHighlights(nextHighlights, textInput, textInput)
    })
    window.getSelection().removeAllRanges()
    setActiveHistorySelection(null)
  }

  const handleSaveHighlight = async () => {
    const currentHighlights = normalizeHighlights(highlightedText, textInput, originalHistoryText)
    setHighlightedText(currentHighlights)
    noteStructureRef.current = {
      ...noteStructureRef.current,
      highlightDetails: {
        ...(noteStructureRef.current.highlightDetails || {}),
        [canBeEditableExistInNoteMetadata]: currentHighlights,
      },
    }
    await persistNoteStructure(true)
    setShowModalHighlight(false)
  }

  const renderHighlightedTextHistory = (text = '', highlights = []) => {
    if (!text) return null

    const objectHighlights = normalizeHighlights(highlights, text, text)

    if (objectHighlights.length > 0) {
      const parts = []
      let lastIndex = 0

      objectHighlights.forEach((h, i) => {
        const start = Math.max(h.start, lastIndex)
        const end = Math.min(h.end, text.length)

        if (start >= end) return

        if (start > lastIndex) {
          parts.push(<span key={`text-${i}`}>{text.slice(lastIndex, start)}</span>)
        }

        parts.push(
          <span key={`highlight-${i}`} style={{ color: 'var(--ui-color-highlight, #ff9f1a)', fontWeight: 600 }}>
            {text.slice(start, end)}
          </span>,
        )

        lastIndex = end
      })

      if (lastIndex < text.length) {
        parts.push(<span key="end">{text.slice(lastIndex)}</span>)
      }

      return <>{parts}</>
    }

    return <span>{text}</span>
  }

  const getDiagnosisDisabled = false
  const addHistoryDisabled = loadings || !textInput.trim()

  return (
    <>
      {/* delete modal */}
      <CModal visible={deleteVisibleId}>
        <CModalHeader>
          <CModalTitle>Warning</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="fs-6 p-0 m-0">Are you sure you want to delete?</p>
        </CModalBody>
        <hr className="p-0 m-0" />
        <CModalFooter className="p-3 m-0">
          <CButton
            onClick={() => {
              setDeleteVisibleId(null)
            }}
            variant="outline"
            color="secondary"
          >
            Close
          </CButton>
          <CButton onClick={handleDeleteHospitalHistory} className="ui-action-btn ui-action-btn--danger" color="danger">
            Delete
          </CButton>
        </CModalFooter>
      </CModal>

      <div>
        <CModal className="dashboard-dark-modal patient-theme-modal patient-labs-modal" size="lg" backdrop="static" visible={modalVisible} onClose={() => setModalVisible(false)}>
          <CModalHeader onClose={() => setModalVisible(false)}>
            <CModalTitle>Diagnosis Details</CModalTitle>
          </CModalHeader>
          <CModalBody
            style={{
              height: '70vh',
              overflowY: 'auto', // optional: adds scroll if content exceeds height
            }}
          >
            <div className="d-flex h-100">
              <div
                style={{
                  overflowY: 'auto',
                  minWidth: '50%',
                  background: 'var(--ui-bg-modal, rgb(40, 40, 40))',
                }}
                className="flex-fill h-100 border p-2 rounded me-3"
              >
                <p style={{ whiteSpace: 'pre-line' }}></p>
                <HighlightedText text={textInput} keywords={Array.from(detectedKeywordsRef.current)}></HighlightedText>
              </div>
              <div
                style={{
                  overflowY: 'auto',
                  minWidth: '50%',
                }}
                className="flex-fill w-50"
              >
                <h5>Detected Diagnosis</h5>
                {possibleDiagnosis.map((diagnosis, index) => (
                  <CBadge key={index} color="info" className="me-4 mr-1 mb-1 d-flex align-items-center gap-2 text-white" style={{ cursor: 'pointer', width: 'min-content' }}>
                    {diagnosis.Name}
                    <span onClick={() => handleRemoveDiagnosis(index)} className="">
                      <X size={15} />
                    </span>
                  </CBadge>
                ))}
                <hr />
                <h5>Existing Diagnosis</h5>
                {diagnosis.map((diagnosisItem, index) => (
                  <CBadge
                    // onClick={() => {
                    //   removeDiagnosisIdFromNote(diagnosisItem)
                    // }}
                    key={index}
                    color="primary"
                    className="me-2 mr-1 mb-1"
                  >
                    {diagnosisItem}
                    <span className="">
                      {/* <X size={15} /> */}
                    </span>
                  </CBadge>
                ))}
              </div>
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton
              variant={possibleDiagnosis.length === 0 ? '' : 'outline'}
              color={possibleDiagnosis.length === 0 ? 'secondary' : 'primary'}
              disabled={possibleDiagnosis.length === 0}
              style={{
                cursor: possibleDiagnosis.length === 0 ? 'not-allowed' : 'pointer',
                opacity: possibleDiagnosis.length === 0 ? 0.6 : 1,
              }}
              onClick={() => {
                if (possibleDiagnosis.length === 0) {
                  toast.error('No possible diagnosis found', toastConfig)
                  return
                }
                addDiagnosis(true)
              }}
            >
              Add Diagnosis
            </CButton>
            <CButton
              variant={possibleDiagnosis.length === 0 ? '' : 'outline'}
              color={possibleDiagnosis.length === 0 ? 'secondary' : 'primary'}
              disabled={possibleDiagnosis.length === 0}
              style={{
                cursor: possibleDiagnosis.length === 0 ? 'not-allowed' : 'pointer',
                opacity: possibleDiagnosis.length === 0 ? 0.6 : 1,
              }}
              onClick={() => {
                if (possibleDiagnosis.length === 0) return
                handleAddHistory()
                setModalVisible(false)
              }}
            >
              Add History
            </CButton>
          </CModalFooter>
        </CModal>
        <CNav variant="pills" className="justify-content-center">
          <CNavItem>
            <CNavLink active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
              New
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink active={activeTab === 'old'} onClick={() => setActiveTab('old')}>
              Past
            </CNavLink>
          </CNavItem>
        </CNav>
        <CTabContent>
          <CTabPane visible={activeTab === 'new'} className="mt-4">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                <CSpinner color="primary" />
              </div>
            ) : (
              <>
                <span className="d-flex align-items-center gap-2">
                  <Graytextarea type="text" id="textInput" placeholder="Enter Hospital Record here..." value={textInput} rows={3} onChange={handleInputChange} />
                </span>

                <CButton
                  style={{
                    marginTop: '10px',
                  }}
                  variant="outline"
                  color="success"
                  disabled={getDiagnosisDisabled}
                  onClick={handleGetDiagnosis}
                >
                  Get Diagnosis
                </CButton>

                <CButton
                  style={{
                    marginTop: '10px',
                    marginLeft: '10px',
                    '--hospital-record-action-color': '#321fdb',
                    opacity: addHistoryDisabled ? 0.65 : 1,
                  }}
                  className="hospital-record-action-btn patient-page-action-btn"
                  onClick={handleAddHistory}
                  variant="outline"
                  color="primary"
                  disabled={addHistoryDisabled}
                >
                  {loadings ? (   
                    <CSpinner component="span" size="sm" />
                  ) : (
                    <>{editRecordId !== null ? 'Update' : 'Add'}</>
                  )}
                </CButton>



                {allHistory
                  ?.filter((obj) => obj.Id === canBeEditableExistInNoteMetadata)
                  .map((obj) => {
                    return (
                      <Carder
                        EditHighlightFunc={
                          obj.Id === canBeEditableExistInNoteMetadata
                            ? () => {
                              setShowModalHighlight(true)
                              setEditableHighlightData(obj)
                              setHighlightedText(normalizeHighlights(noteStructureRef.current?.highlightDetails?.[obj.Id] || [], obj.History || '', obj.History || ''))
                            }
                            : null
                        }
                        deleteFunc={
                          obj.Id === canBeEditableExistInNoteMetadata
                            ? () => {
                              setDeleteVisibleId(obj.Id)
                            }
                            : null
                        }

                        style={{ borderRadius: '1rem', boxShadow: '0px 4px 6px rgba(216, 165, 165, 0.1)' }}
                        className=""
                      >
                        <CCardBody>
                          <div className="p-0 m-0" style={{ whiteSpace: 'pre-line' }}>
                            {renderHighlightedTextHistory(obj.History || '', noteStructureRef.current?.highlightDetails?.[obj.Id] || [])}
                          </div>
                        </CCardBody>
                      </Carder>
                    )
                  })}
              </>
            )}
          </CTabPane>
          <CTabPane visible={activeTab === 'old'}>
            {noteStructureRef.current?.freeTypesEverything?.hospitalRecordsFreetype ? (
              <div className="mt-2">
                <div
                  onClick={() => {
                    setTempHtmlContent(noteStructureRef.current.freeTypesEverything.hospitalRecordsFreetype)
                    // setShowEditHtml(true)
                  }}
                  className="p-4 patient-theme-old-data patient-theme-old-data--readonly"
                  dangerouslySetInnerHTML={{
                    __html: formatFreeTypeHtml(noteStructureRef.current.freeTypesEverything.hospitalRecordsFreetype),
                  }}
                />
              </div>
            ) : allHistory?.length > 0 ? (
              <>
                {allHistory.map((obj) => {
                  const isEditable = obj.Id === canBeEditableExistInNoteMetadata

                  return (
                    <Carder
                      key={obj.Id}
                      deleteFunc={isEditable ? () => setDeleteVisibleId(obj.Id) : null}
                      editFunc={isEditable ? () => handleEditHospitalRecords(obj) : null}
                      style={{
                        borderRadius: '1rem',
                        boxShadow: '0px 4px 6px rgba(216, 165, 165, 0.1)',
                      }}
                    >
                      <CCardBody>
                        <div className="p-0 m-0" style={{ whiteSpace: 'pre-line' }}>
                          {renderHighlightedTextHistory(obj.History || '', noteStructureRef.current?.highlightDetails?.[obj.Id] || [])}
                        </div>
                      </CCardBody>
                    </Carder>
                  )
                })}
              </>
            ) : (
              <div className="text-muted mt-3 text-center">No Hospital Records Found.</div>
            )}


          </CTabPane>
        </CTabContent>
      </div>
      <CModal className="patient-note-form-modal" size="lg" scrollable backdrop="static" visible={showModalHighlight} onClose={() => setShowModalHighlight(false)} aria-labelledby="LiveDemoExampleLabel">
        <CModalHeader>
          <CModalTitle id="LiveDemoExampleLabel">Select the text to highlight</CModalTitle>
        </CModalHeader>
        <CModalBody
          onMouseUp={handleSelection}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >


          <div ref={historyHighlightRef} className="p-4 m-0" data-impression="history" style={{ whiteSpace: 'pre-line' }}>
            <div>{renderHighlightedTextHistory(textInput, highlightedText)}</div>
          </div>

          <div className="d-flex gap-2 align-items-center">


            <CButton
              variant="outline"
              style={{ fontSize: '12px' }}
              color="primary"
              onClick={() => {
                if (!activeHistorySelection) return
                if (selectionFullyHighlighted(activeHistorySelection, highlightedText)) {
                  handleRemoveSelections()
                } else {
                  handleAddSelections()
                }
              }}
            >
              {activeHistorySelection && selectionFullyHighlighted(activeHistorySelection, highlightedText) ? 'Remove Highlight' : 'Add Highlight'}
            </CButton>

          </div>
        </CModalBody>
        <CModalFooter>
          <CButton variant="outline" color="secondary" onClick={() => setShowModalHighlight(false)}>
            Close
          </CButton>
          <CButton color="primary" onClick={handleSaveHighlight}>
            Save
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal size="lg" visible={showEditHtml} onClose={() => setShowEditHtml(false)} aria-labelledby="OptionalSizesExample2">
        <CModalHeader>
          <CModalTitle id="OptionalSizesExample2">Edit Hospital Records Free Type</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <Editor
            onChange={(e) => {
              setTempHtmlContent(e.target.value)
            }}
            containerProps={{
              style: {
                width: '100%',
                background: '#f9f9f9',
                border: 0,
                height: '100%',
                minHeight: 'unset',
              },
            }}
            value={tempHtmlContent}
          >
            <tooltip />
          </Editor>
        </CModalBody>
        <CModalFooter>
          <CButton variant="outline" color="secondary" onClick={() => setShowEditHtml(false)}>
            Close
          </CButton>
          <CButton
            color="primary"
            onClick={() => {
              noteStructureRef.current = {
                ...noteStructureRef.current,
                freeTypesEverything: {
                  ...noteStructureRef.current?.freeTypesEverything,
                  hospitalRecordsFreetype: tempHtmlContent,
                },
              }
              saveTreatsShell(false)
              setShowEditHtml(false)
            }}
          >
            Save
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default PatientHistoryForm
